/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { createContext, useReducer } from "react";

export const initialContext = {
  account: {
    loginMessage: null,
    adminLoaded: false,
    admin: false,
    subAdmin: false,
    token: null,
    clientId: null,
    clientSummary: null,
    clientInfo: null,
    clientList: null,
    locationList: null,
    routerList: null,
    captivePortalList: null,
    captivePortalLayoutList: null,
    addLoginPagePrompt: false,
    accountList: null,
    chartData: null,
    chartScope: null,
    activityList: null,
    userList: null,
    userLocationList: null,
    username: null,
    settingsPage: "",
    initialViewState: null,
    adminClientList: [],
    adminLocationList: [],
    adminRouterList: [],
    adminCaptivePortalLayoutList: [],
    adminBroadcastList: [],
    adminAutomationList: [],
    adminMarketingList: [],
    adminAccountList: [],
    adminTemplateList: [],
    sendgridConfigurations: null,
    sendgridSenders: null,
    sendgridSender: null,
    sendgridClientSenders: null,
    twilioSmsConfigurations: null,
    twilioSmsSupported: null,
    plivoSmsConfigurations: null,
    plivoSmsSupported: null,
    turboSmsConfigurations: null,
    turboSmsSupported: null,
    emailCannedMessages: null,
    smsCannedMessages: null,
    marketingProductAndServices: null,
    marketingBroadcasts: null,
    marketingCampaigns: null,
    marketingCampaign: null,
    marketingHighlightType: null,
    marketingHighlightId: null,
    marketingTemplate: null,
    marketingTemplates: null,
    marketingTemplateName: null,
    mailchimpConfig: null,
    mailchimpLists: null,
    mailchimpSyncStatuses: null,
    zapierConfig: null,
    emailEditorHtmlCallback: null,
    brandId: null,
    brandList: null,
    brands: null,
    brand: null,
    permissions: {
      editTemplates: false,
      editLayouts: false,
      provisionBusiness: false,
    }
  },
  captivePortal: {
    client: null,
    config: null,
    layoutList: [],
    layout: null,
    initialLayout: null,
    imageList: [],
    script: null,
    package: null,
    deletePending: null,
    highlightId: ""
  },
  createLoginPage: {
    initialLayout: null,
    clientId: null,
    template: null,
    postLoginPreview: null,
    postLoginPreviewType: null,
    preparePreview: false,
    suggestedName: null,
    stepIndex: 0,
    clientScope: null,
  },
  dashboard: {
    layouts: null,
    layoutIndex: 0,
    layoutSave: 0,
    layoutSaving: false,
    editing: false,
    forceNavbarReload: 0,
    dataScope: "monthly",
    locationScope: "",
    locationFilter: null,
    loginTypePieData: null,
    loyaltyPieData: null,
    gdprPieData: null,
    visitsPieData: null,
    ageBarData: null,
    durationBarData: null,
    loyaltyBarData: null,
    loginMetricsData: null,
    activeLoginPageData: null
  },
  dashboardNavbar: {
    layouts: [],
    layoutIndex: 0,
    editing: false
  },
  login: {
    loginToken: null,
    connectLoginToken: null,
    loginUser: null,
    localization: "",
    identity: null,
    deviceMac: null,
    credentials: null,
    loading: false,
    loginType: null,
    passcode: "",
    name: "",
    country: null,
    state: "",
    email: "",
    sms: "",
    smsCountryCode: null,
    phoneNumber: "",
    birthDay: 1,
    birthMonth: 1,
    birthYear: null,
    customTextField: "",
    verificationCode: "",
    verificationRequired: false,
    correctPasscode: null,
    preloginPreview: false,
    tosAccepted: false,
    tosHtml: null,
    gdprEnabled: false,
    gdprAccepted: null,
    gdprFormEnabled: false,
    gdprForm: null,
    previewEmailVerify: false,
    previewSmsVerify: false,
    autoConnect: false,
  },
  provisioning: {
    business: {
      general: {},
      contact: {},
      address: {},
      image: {},
      loginPage: {},
      hotspotConfig: {},
    },
    location: {
      general: {},
      contact: {},
      address: {},
    },
    router: {
      general: {},
    },
    account: {
      general: {},
      login: {},
      permissions: {},
    },
    googleReview: {
      search: null,
      dataId: null,
      placeId: null,
    },
    layoutList: [],
    layoutPreview: {},
    responseClient: null,
    step: 0
  },
  requestNavigate: {
    handleNavigate: null,
    handleCompletion: null,
    showLoading: false,
    requests: [],
    retries: null,
    pending: false,
  },
  router: {
    routerList: null,
    router: null,
  },
  server: {
    certificate: null
  },
  temp: { /* Used to store temporary request results */ }
};

export const ContextUpdater = createContext(null);
export const AccountContext = createContext(initialContext.account);
export const CaptivePortalContext = createContext(initialContext.captivePortal);
export const CreateLoginPageContext = createContext(initialContext.createLoginPage);
export const DashboardContext = createContext(initialContext.dashboard);
export const DashboardNavbarContext = createContext(initialContext.dashboardNavbar);
export const LoginContext = createContext(initialContext.login);
export const MultistepFormContext = createContext(initialContext.multistepForm);
export const ProvisioningContext = createContext(initialContext.provisioning);
export const RequestNavigateContext = createContext(initialContext.requestNavigate);
export const RouterContext = createContext(initialContext.router);
export const ServerContext = createContext(initialContext.server);

const reducer = (state, action) => {
  if (!(action.type in state)) {
    throw new Error("ContextProvider.reduce(): action.type not found: " + action.type);
  }
  return {
    ...state,
    [action.type]: {
      ...state[action.type],
      ...action.payload
    }
  };
};

const ContextProvider = ({ children }) => {
  // general statte
  const [state, dispatch] = useReducer(reducer, initialContext);

  // context update
  const contextUpdaterValue = (newState) => {
    Object.entries(newState).forEach(([key, value]) => {
      dispatch({ type: key, payload: value });
    });
  };

  // context update state callbacks
  const contextNames = Object.keys(initialContext);
  const contextValues = {};
  for (const key of contextNames) {
    contextValues[key] = {
      ...state[key],
      updateState: (newState) => dispatch({ type: key, payload: newState })
    };
  }

  // custom callbacks
  contextValues.dashboardNavbar["getDashboardLayouts"] = () => { return state.dashboard.layouts };

  return (
    <ContextUpdater.Provider value={contextUpdaterValue}>
    <AccountContext.Provider value={contextValues.account}>
    <CaptivePortalContext.Provider value={contextValues.captivePortal}>
    <CreateLoginPageContext.Provider value={contextValues.createLoginPage}>
    <DashboardContext.Provider value={contextValues.dashboard}>
    <DashboardNavbarContext.Provider value={contextValues.dashboardNavbar}>
    <LoginContext.Provider value={contextValues.login}>
    <MultistepFormContext.Provider value={contextValues.multistepForm}>
    <ProvisioningContext.Provider value={contextValues.provisioning}>
    <RequestNavigateContext.Provider value={contextValues.requestNavigate}>
    <RouterContext.Provider value={contextValues.router}>
      {children}
    </RouterContext.Provider>
    </RequestNavigateContext.Provider>
    </ProvisioningContext.Provider>
    </MultistepFormContext.Provider>
    </LoginContext.Provider>
    </DashboardNavbarContext.Provider>
    </DashboardContext.Provider>
    </CreateLoginPageContext.Provider>
    </CaptivePortalContext.Provider>
    </AccountContext.Provider>
    </ContextUpdater.Provider>
  );
};

export default ContextProvider;
